import { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  selectClinicId,
  selectTherapistId,
} from "../../store/slices/authSlice";
import { useGetAppointmentsQuery } from "../../store/services/calendarApi";
import { useDayKey } from "../../hooks/useDayKey";
import { Timer, ArrowLeft } from "@phosphor-icons/react";
import ModalCloseButton from "../ui/ModalCloseButton";
import "./MobileLiveSessionBanner.css";

/**
 * MobileLiveSessionBanner - Bottom banner for live sessions
 * 
 * Displays a prominent full-width banner at the bottom of the screen
 * when the therapist has an active appointment in progress.
 * Hidden when already on the live session page.
 * Can be dismissed by user (reappears for new appointments).
 */
function MobileLiveSessionBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isDismissed, setIsDismissed] = useState(false);
  const dismissedAppointmentId = useRef(null);

  // Check if we're already on the live session page
  const isOnLiveSessionPage = location.pathname.includes("/live-session");

  // Update current time every second for accurate countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Get today's date range for fetching appointments (rolls over at midnight)
  const dayKey = useDayKey();
  const today = useMemo(() => {
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);
    return { startOfDay, endOfDay };
  }, [dayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch today's appointments
  const { data: appointments = [] } = useGetAppointmentsQuery(
    {
      clinicId,
      startDate: today.startOfDay.toISOString(),
      endDate: today.endOfDay.toISOString(),
      therapistId,
    },
    {
      skip: !clinicId || !therapistId,
      pollingInterval: 60000,
    }
  );

  // Find ongoing appointment
  const ongoingAppointment = useMemo(() => {
    if (!appointments || appointments.length === 0) return null;

    const now = currentTime.getTime();

    return appointments.find((apt) => {
      const startTime = new Date(apt.startTime).getTime();
      const endTime = new Date(apt.endTime).getTime();
      const isOngoing = now >= startTime && now <= endTime;
      const isActive = apt.status === "scheduled" || apt.status === "confirmed";
      // Temp customers (לקוח מזדמן) have no live session
      return isOngoing && isActive && apt.isTempCustomer !== true;
    });
  }, [appointments, currentTime]);

  // Calculate remaining time
  const remainingTime = useMemo(() => {
    if (!ongoingAppointment) return null;

    const endTime = new Date(ongoingAppointment.endTime).getTime();
    const now = currentTime.getTime();
    const diffMs = endTime - now;

    if (diffMs <= 0) return { minutes: 0, seconds: 0, formatted: "00:00" };

    const totalSeconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    const formatted = `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;

    return { minutes, seconds, formatted };
  }, [ongoingAppointment, currentTime]);

  // Reset dismissed state when appointment changes
  useEffect(() => {
    if (ongoingAppointment?.id && ongoingAppointment.id !== dismissedAppointmentId.current) {
      setIsDismissed(false);
      dismissedAppointmentId.current = null;
    }
  }, [ongoingAppointment?.id]);

  // Don't render if no ongoing appointment, already on live session page, or dismissed
  if (!ongoingAppointment || isOnLiveSessionPage || isDismissed) return null;

  const handleClick = () => {
    navigate(`/dashboard/live-session/${ongoingAppointment.id}?patientId=${ongoingAppointment.patientId}`);
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    setIsDismissed(true);
    dismissedAppointmentId.current = ongoingAppointment.id;
  };

  // Get first name only for cleaner display
  const patientFirstName = ongoingAppointment.patientName?.split(" ")[0] || "מטופל";

  return (
    <div className="mobile-live-session-banner" onClick={handleClick}>
      {/* Dismiss button - at the start (right side in RTL), away from arrow */}
      <ModalCloseButton className="mlsb-dismiss" size={18} onClick={handleDismiss} />
      
      {/* Live Pulse Dot */}
      <div className="mlsb-live-dot" />
      
      {/* Content */}
      <div className="mlsb-content">
        <span className="mlsb-label">
          פגישה פעילה עם <strong>{patientFirstName}</strong>
        </span>
        
        {/* Timer */}
        <div className="mlsb-timer">
          <Timer size={16} weight="bold" />
          <span>{remainingTime?.formatted}</span>
        </div>
      </div>
      
      {/* Arrow indicator */}
      <ArrowLeft size={20} weight="bold" className="mlsb-arrow" />
    </div>
  );
}

export default MobileLiveSessionBanner;
